import { Plus, Workflow as WorkflowIcon } from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../../atomic-crm/root/ConfigurationContext";
import { EmptyState } from "../data-display/EmptyState";
import { SectionCard } from "../data-display/SectionCard";
import { StatusBadge } from "../data-display/StatusBadge";
import { ListPageHeader } from "../list/ListPageHeader";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { WorkflowActionsEditor } from "./WorkflowActionsEditor";
import { WorkflowRunsPanel } from "./WorkflowRunsPanel";
import {
  TRIGGERS,
  type Workflow,
  triggerDefinition,
  triggerLabel,
} from "./workflowModel";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Workflow builder: when THIS happens, do THAT. Triggers are real database
 * events, so automation fires no matter which surface caused the change;
 * actions run asynchronously and every attempt lands on the run ledger.
 */
export const WorkflowsPage = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");

  const { data: workflows, isPending } = useGetList<Workflow>("workflows", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
  });

  if (isPending) return null;

  const selected = (workflows ?? []).find((w) => w.id === selectedId);

  const createWorkflow = () => {
    if (!newName.trim()) {
      notify("Name the workflow first", { type: "warning" });
      return;
    }
    create(
      "workflows",
      {
        data: {
          name: newName.trim(),
          trigger_type: "contact.created",
          workspace_id: getActiveWorkspaceId(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: (record: Workflow) => {
          notify("Workflow created, it stays off until you turn it on");
          setNewName("");
          setSelectedId(record.id);
          refresh();
        },
        onError: (error: unknown) =>
          notify(error instanceof Error ? error.message : "Could not create", {
            type: "error",
          }),
      },
    );
  };

  return (
    <>
      <ListPageHeader
        title="Workflows"
        subtitle="When this happens, do that, automatically"
        actions={
          <div className="flex items-center gap-2">
            <input
              className={cn(inputClass, "w-56")}
              placeholder="New workflow name…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") createWorkflow();
              }}
            />
            <Button size="sm" onClick={createWorkflow}>
              <Plus className="mr-1 size-3.5" />
              Create
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-[280px_1fr]">
        <Card className="h-fit">
          <CardContent className="flex flex-col gap-0.5 py-3">
            {(workflows ?? []).length === 0 ? (
              <p className="px-2 py-4 text-[13px] text-muted-foreground">
                No workflows yet.
              </p>
            ) : (
              (workflows ?? []).map((workflow) => (
                <button
                  key={workflow.id}
                  type="button"
                  onClick={() => setSelectedId(workflow.id)}
                  className={cn(
                    "flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors",
                    selectedId === workflow.id
                      ? "bg-accent text-foreground"
                      : "text-muted-foreground hover:bg-accent/50",
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {workflow.name}
                    </span>
                    <span className="block truncate text-[11.5px]">
                      {triggerLabel(workflow.trigger_type)}
                    </span>
                  </span>
                  <StatusBadge
                    label={workflow.active ? "on" : "off"}
                    tone={workflow.active ? "success" : "neutral"}
                  />
                </button>
              ))
            )}
          </CardContent>
        </Card>

        {selected ? (
          <WorkflowDetail workflow={selected} />
        ) : (
          <SectionCard title="Workflows">
            <div className="py-8">
              <EmptyState
                icon={WorkflowIcon}
                title="Pick or create a workflow"
                description="Automate follow-ups, stage changes, tagging and job creation from real events."
              />
            </div>
          </SectionCard>
        )}
      </div>
    </>
  );
};

const WorkflowDetail = ({ workflow }: { workflow: Workflow }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const { dealStages } = useConfigurationContext();

  const { data: forms } = useGetList<{ id: string; name: string }>("forms", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const [triggerType, setTriggerType] = useState(workflow.trigger_type);
  const [condition, setCondition] = useState<string>("");
  const [description, setDescription] = useState(workflow.description ?? "");
  const [loadedId, setLoadedId] = useState(workflow.id);

  const definition = triggerDefinition(triggerType);

  if (loadedId !== workflow.id) {
    setLoadedId(workflow.id);
    setTriggerType(workflow.trigger_type);
    setDescription(workflow.description ?? "");
    const key = triggerDefinition(workflow.trigger_type)?.conditionKey;
    setCondition(key ? ((workflow.trigger_config ?? {})[key] ?? "") : "");
  }

  const saveTrigger = () => {
    const key = definition?.conditionKey;
    update(
      "workflows",
      {
        id: workflow.id,
        data: {
          trigger_type: triggerType,
          description: description.trim() || null,
          trigger_config: key && condition ? { [key]: condition } : {},
        },
        previousData: workflow,
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Trigger saved");
          refresh();
        },
        onError: (error: unknown) =>
          notify(error instanceof Error ? error.message : "Could not save", {
            type: "error",
          }),
      },
    );
  };

  const toggleActive = () => {
    update(
      "workflows",
      {
        id: workflow.id,
        data: { active: !workflow.active },
        previousData: workflow,
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify(workflow.active ? "Workflow turned off" : "Workflow is live");
          refresh();
        },
        onError: (error: unknown) =>
          notify(error instanceof Error ? error.message : "Could not update", {
            type: "error",
          }),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon={WorkflowIcon}
        title={workflow.name}
        subtitle={
          workflow.active
            ? "Live, running on every matching event"
            : "Off, no events are processed"
        }
      >
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              When
              <select
                className={inputClass}
                value={triggerType}
                onChange={(e) => {
                  setTriggerType(e.target.value);
                  setCondition("");
                }}
              >
                {TRIGGERS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>

            {definition?.conditionKey === "to_stage" && (
              <label className="flex flex-col gap-1 text-[12.5px] font-medium">
                {definition.conditionLabel}
                <select
                  className={inputClass}
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                >
                  <option value="">Any stage</option>
                  {dealStages.map((stage) => (
                    <option key={stage.value} value={stage.value}>
                      {stage.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {definition?.conditionKey === "form_id" && (
              <label className="flex flex-col gap-1 text-[12.5px] font-medium">
                {definition.conditionLabel}
                <select
                  className={inputClass}
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                >
                  <option value="">Any form</option>
                  {(forms ?? []).map((form) => (
                    <option key={form.id} value={form.id}>
                      {form.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Description
            <input
              className={inputClass}
              placeholder="What this automation is for"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <div className="flex items-center justify-between border-t border-border-subtle pt-3">
            <Button size="sm" variant="outline" onClick={toggleActive}>
              {workflow.active ? "Turn off" : "Turn on"}
            </Button>
            <Button size="sm" onClick={saveTrigger}>
              Save trigger
            </Button>
          </div>
        </div>
      </SectionCard>

      <WorkflowActionsEditor workflow={workflow} />
      <WorkflowRunsPanel workflowId={workflow.id} />
    </div>
  );
};
